// getProp returns the property value given its key
function getProp(obj, key) {
    return obj[key];
}
// hasSameProp checks if 'target' has the same properties as 'reference'.
// check if the property is existent and also has the same type as reference
function hasSameProp(reference, target, key) {
    // check if property exists
    if (!Object.prototype.hasOwnProperty.call(target, key)) {
        return false;
    }
    // check if the types match
    const typ = typeof reference[key];
    const val = target[key];
    if (typeof val !== typ) {
        return false;
    }
    return true;
}

// logErr logs the error in any2type
const logErr = (obj, key = '') => {
    if (!obj) {
        console.log('[ERROR] object is null');
        return;
    }
    console.log(`[ERROR] object = ${JSON.stringify(obj)}`);
    console.log(`[ERROR] property for key = ${key} doesn't exist or has incorrect type`);
};

/**
 * checkType hierarchically compares 'any' to a 'reference'
 * @param reference - the aimed typed object
 * @param data - the input 'Iany' data to be 'typed'
 * @param optionalShallowFields - optional fields (shallow only)
 * @param verbose - show (nested) messages on errors
 * @returns the 'typed' object if ok; or null if the types are not compatible
 * @remarks
 * ```
 * 1. The 'data' object may contain more fields than the 'reference' object
 * 2. Only the first fields may be optional (shallow)
 * 3. It doesn't matter if the entries in optionalShallowFields are true or false
 * 4. This function is recursive
 * ```
 */
function checkType(reference, data, optionalShallowFields, verbose = false) {
    // check for non-null target
    if (!data) {
        if (verbose) {
            logErr(data);
        }
        return null; // failed
    }
    // compare keys and types, hierarchically
    for (const key of Object.keys(reference)) {
        if (optionalShallowFields) {
            if (Object.prototype.hasOwnProperty.call(optionalShallowFields, key)) {
                continue; // skip optional key
            }
        }
        if (!hasSameProp(reference, data, key)) {
            return null;
        }
        const subref = reference[key];
        if (Array.isArray(subref)) {
            if (subref.length !== 1) {
                throw new Error('each reference array must contain a single item');
            }
            const subdata = data[key];
            if (!Array.isArray(subdata)) {
                return null; // failed
            }
            const subsubref = subref[0];
            for (const subsubdata of subdata) {
                const res = checkType(subsubref, subsubdata, undefined, verbose);
                if (res === null) {
                    return null; // failed
                }
            }
        }
        else if (typeof subref === 'object') {
            const subdata = data[key];
            const res = checkType(subref, subdata, undefined, verbose);
            if (res === null) {
                return null; // failed
            }
        }
    }
    // success
    return data;
}

/**
 * checkTypes hierarchically compares and array of 'any' to an array of 'reference'
 * @param reference - the aimed typed object
 * @param dataArray - the input array of 'Iany' to be 'typed'
 * @param optionalShallowFields - optional fields (shallow only)
 * @param verbose - show (nested) messages on errors
 * @returns the 'typed' array if ok; or null if the types are not compatible
 * @remarks
 * ```
 * 1. The objects in the 'data' array may contain more fields than the 'reference' object
 * 2. Only the first fields may be optional (shallow)
 * 3. It doesn't matter if the entries in optionalShallowFields are true or false
 * ```
 */
function checkTypes(reference, dataArray, optionalShallowFields, verbose = false) {
    // check for non-null target
    if (!dataArray) {
        if (verbose) {
            logErr(dataArray);
        }
        return null; // failed
    }
    // empty array
    if (dataArray.length === 0) {
        return []; // ok
    }
    // check each single item
    for (const item of dataArray) {
        const res = checkType(reference, item, optionalShallowFields, verbose);
        if (res === null) {
            return null; // failed
        }
    }
    // success
    return dataArray;
}

export { checkType, checkTypes, getProp, hasSameProp, logErr };
